import { EMPTY, Observable } from 'rxjs';

import {map} from 'rxjs/operators';
import { Injectable, Injector, inject } from '@angular/core';
import jwt_decode from 'jwt-decode';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie';

import { Wrapper } from '../models/wrapper.model';
import { UserProfile } from '../models/user-profile.model';
import {
    AccountOutput,
    ActivateAccountOutput,
    JwtUserClaims,
    MemberAccountOutput,
    PrivilegeOutput,
    SaveAccountInput,
    UpdateAccountInput,
    UserListOutput
} from '../models/security-contracts';
import { environment } from 'environments/environment';

@Injectable()
export class UsuarioService {
    private http = inject(HttpClient);
    private injector = inject(Injector);

    securityApi = `${environment.api}/svc-security/api`;

    byMember(idenMembro: number): Observable<Wrapper<MemberAccountOutput>> {
        return this.http.get<Wrapper<MemberAccountOutput>>(`${this.securityApi}/account/by-member/${idenMembro}`);
    }

    byEmail(email: string): Observable<Wrapper<boolean>> {
        email = encodeURIComponent(email);
        return this.http.get<Wrapper<boolean>>(`${this.securityApi}/account/exists/by-mail/${email}`);
    }

    changeBasedOnProfiles(userProf: UserProfile): Observable<Wrapper<number>> {
        return this.http.put<Wrapper<number>>(`${this.securityApi}/authorization/change-based-on-profiles`, userProf);
    }

    changePassword(token: string, senhCont: string): Observable<Wrapper<boolean>> {
        const body = { senhCont: senhCont };
        return this.http.put<Wrapper<boolean>>(`${this.securityApi}/account/change-password/${token}`, body);
    }

    updatePassword(lognCont: string, novaSenh: string, senhCont: string): Observable<Wrapper<boolean>> {
        const body = { lognCont: lognCont, novaSenh: novaSenh, senhCont: senhCont };

        return this.http.put<Wrapper<boolean>>(`${this.securityApi}/account/change-password/`, body);
    }

    getObjetosByConta(idenCont: number, types: string[]): Observable<Wrapper<PrivilegeOutput[]>> {
        let params = '';
        if (types && types.length > 0) {
            params = '?types=' + types.join('&types=');
        }

        return this.http.get<Wrapper<PrivilegeOutput[]>>(`${this.securityApi}/authorization/privileges${params}`);
    }

    getRole(): string[] {
        const cookieService = this.injector.get(CookieService);
        // Os perfis (role) saíram do id_token — ficam SÓ no access token (opaco), para reduzir o
        // peso do cookie de domínio .oab.org.br (nginx 400 nos apps legados). Os consumidores de
        // getRole() têm o filtro por perfil desativado (o menu já vem filtrado do servidor via
        // menu-usr-login), então lista vazia é o comportamento correto para tokens novos.
        const role = jwt_decode<JwtUserClaims>(cookieService.get(environment.oab_id_token)).role;
        if (!role) {
            return [];
        }
        if (Array.isArray(role)) {
            return role;
        } else {
            return [role];
        }
    }
    getUser(): JwtUserClaims {
        const cookieService = this.injector.get(CookieService);
        return jwt_decode<JwtUserClaims>(cookieService.get(environment.oab_id_token));
    }
    getIdenOgan(): string {
        const cookieService = this.injector.get(CookieService);
        return cookieService.get(environment.oab_organizacao_padrao);
    }
    list(nome: string, page = 0, size = 10): Observable<MemberAccountOutput[]> {
        if (nome) {
            const nomeLike = nome ? `nomeLike=${nome}&` : '';
            return this.http.get<Wrapper<{ results: MemberAccountOutput[] }>>(
                `${this.securityApi}/account?${nomeLike}skip=${(page - 1) * size}&take=${size}`
            ).pipe(map(res => res.data?.results ?? []));
        } else {
            return EMPTY;
        }
    }

    listAll(nome = '', page = 1, size = 10): Observable<{ results: MemberAccountOutput[], total: number }> {
        const nomeLike = nome ? `nomeLike=${encodeURIComponent(nome)}&` : '';
        return this.http.get<Wrapper<{ results: MemberAccountOutput[], totalRecords: number }>>(
            `${this.securityApi}/account?${nomeLike}skip=${(page - 1) * size}&take=${size}`
        ).pipe(map(res => ({ results: res.data?.results ?? [], total: res.data?.totalRecords ?? 0 })));
    }

    listUsers(filters: {
        nome?: string;
        cpf?: string;
        email?: string;
        idenOgan?: number;
        codiSetr?: number;
        situacao?: string;
    } = {}, page = 1, size = 10): Observable<{ results: UserListOutput[], total: number }> {
        const params = new URLSearchParams();
        if (filters.nome) params.set('nome', filters.nome);
        if (filters.cpf) params.set('cpf', filters.cpf.replace(/\D/g, ''));
        if (filters.email) params.set('email', filters.email);
        if (filters.idenOgan) params.set('idenOgan', String(filters.idenOgan));
        if (filters.codiSetr) params.set('codiSetr', String(filters.codiSetr));
        if (filters.situacao) params.set('situacao', filters.situacao);
        params.set('skip', String((page - 1) * size));
        params.set('take', String(size));

        return this.http.get<Wrapper<{ results: UserListOutput[], totalRecords: number }>>(
            `${this.securityApi}/account/list-users?${params.toString()}`
        ).pipe(map(res => ({ results: res.data?.results ?? [], total: res.data?.totalRecords ?? 0 })));
    }

    search(idenTipoCont: string, nome: string, page = 0, size = 10): Observable<MemberAccountOutput[]> {
        const nomeLike = nome ? `nomeLike=${nome}&` : '';
        return this.http.get<Wrapper<{ results: MemberAccountOutput[] }>>(
            `${this.securityApi}/account/search?idenTipoCont=${idenTipoCont}&${nomeLike}skip=${(page - 1) * size}&take=${size}`
        ).pipe(map(res => res.data?.results ?? []));
    }

    listSistemas(nome: string, page = 0, size = 10): Observable<AccountOutput[]> {
        const nomeLike = nome ? `nomeLike=${nome}&` : '';
        return this.http.get<Wrapper<{ results: AccountOutput[] }>>(
            `${this.securityApi}/account/by-type?idenTipoCont=SIS&${nomeLike}skip=${(page - 1) * size}&take=${size}`
        ).pipe(map(res => res.data?.results ?? []));
    }

    requestPasswordReset(lognCont: string, callbackUrl?: string) : Observable<Wrapper<boolean>> {
        const resetAbsoluteUrl = `${window.location.protocol}//${window.location.host}/pages/reset/{token}?callbackUrl=${callbackUrl}`;
        const body = { lognCont: lognCont, resetAbsoluteUrl: resetAbsoluteUrl };
        return this.http.post<Wrapper<boolean>>(`${this.securityApi}/account/request-password-reset`, body);
    }

    save(usuario: SaveAccountInput): Observable<Wrapper<AccountOutput>> {
        const activationUrl = encodeURIComponent(`${window.location.protocol}//${window.location.host}/pages/ativacao/{token}`)
        return this.http.post<Wrapper<AccountOutput>>(`${this.securityApi}/account?activationAbsoluteUrl=${activationUrl}`, usuario);
    }

    update(idenCont: number, usuario: UpdateAccountInput): Observable<Wrapper<AccountOutput>> {
        return this.http.put<Wrapper<AccountOutput>>(`${this.securityApi}/account/${idenCont}`, usuario);
    }

    saveSistema(sistema: SaveAccountInput): Observable<Wrapper<AccountOutput>> {
        return this.http.post<Wrapper<AccountOutput>>(`${this.securityApi}/account`, sistema);
    }

    activate(token: string, password: string): Observable<Wrapper<ActivateAccountOutput>> {
        return this.http.post<Wrapper<ActivateAccountOutput>>(`${this.securityApi}/account/activate/${token}`, { password })
    }

    resendActivationLink(idenCont: number): Observable<Wrapper<boolean>> {
        const activationUrl = encodeURIComponent(`${window.location.protocol}//${window.location.host}/pages/ativacao/{token}`)
        return this.http.post<Wrapper<boolean>>(`${this.securityApi}/account/${idenCont}/resend-activation-link?activationAbsoluteUrl=${activationUrl}`, {})
    }

}
